import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertBanner, AlertBannerStatus, GlossaryTooltip, NotificationDispatch, RoomProgressBar, StyledButton, SwitchThemeContext, useViewport } from '@tryhackme/thm-ui-components';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { SEO } from 'src/common/components/seo';
import { HttpStatus, ModalType, RoomDetailsStatusCodes, ThemeColorMode } from 'src/common/enums';
import { RoomTaskQuestion } from 'src/common/interfaces/tasks';
import { clearSplitScreen } from 'src/app/reducers/split-screen.reducer';
import { setCurrentModal } from 'src/app/reducers/modal-states.reducer';
import { clearTutorState, closeTutor } from 'src/app/reducers/room/tutor.reducer';
import { useGetFeatureFlagsQuery, useGetUserQuery, useTrackViewPageMutation } from 'src/common/slices';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setHasUserJoinedRoom, setIsRoomComplete, setIsVmReady, setRemAttackBoxBtn, setRoomCode } from 'src/app/reducers/room/room.reducer';
import { useGetVms, useHelperTooltip } from 'src/common/hooks';
import { InstanceTerminationModal } from 'src/features/room/components/instance-termination-modal';
import { addSocketListener, emitSocketEvent, removeSocketListener } from 'src/common/sockets';
import { RoomUserEventType, VideoSource } from 'src/common/enums/rooms';
import { useRoomHeartBeat } from 'src/common/hooks/use-heartbeat';
import { Video } from 'src/features/room/components/complex-room-box/components';
import { useGetGlossaryQuery } from 'src/features/glossary/glossary.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { FeatureFlagName } from 'src/common/constants';
import { getLegacyUrlString } from 'src/router/route-protector';
import { LocationState } from 'src/common/interfaces/analytics';
import { redirectToPath } from 'src/common/helpers';
import { useRoomGlossaryTooltip } from 'src/features/room/components/tasks/components/accordion/hooks/use-room-glossary-tooltip';
import { StyledGlossaryTooltipContent, StyledGlossaryTooltipWrapper } from 'src/features/room/components/tasks/components/accordion/accordion.styles';
import { PathOutlineModal } from 'src/common/components/path-outline-modal';
import { PageTypes } from 'src/common/enums/segment';
import { Tutor } from 'src/features/room/components/tutor/tutor';
import { useGetAzureLabsQuery } from 'src/features/api/rooms/azure/azure.slice';
import { SocSimAdBanner, StandardActionModal } from 'src/common/components';
import { StyledModalIcon } from 'src/common/components/standard-action-modal/standard-action-modal.styles';
import { TaskType } from 'src/common/enums/task';
import { useGetCurrentTeamQuery } from 'src/features/manage-account/teams/team.slice';
import { CampaignName } from 'src/common/enums/campaigns';
import { useCampaignSession } from '../campaigns/hooks/use-campaign-session';
import { RoomContent, RoomFooter, RoomHeader } from './components/task-breakdown';
import { AlertBannerContainer, SocSimAdContainer, StyledContainer, StyledInfoBox, StyledLink, StyledRoomContent, StyledVideoSection } from './room.styles';
import { ActiveLabInfo, ActiveMachineInfo, ComplexRoomBox, RoomBanner, RoomDetailsFooter, Tasks, CongratulationFullScreen } from './components';
import { ERROR_CONTACT_MSG, ERROR_ROOM_NOT_FOUND, ROOM_EVENTS_JOIN, ROOM_JOIN_TEAM, ROOM_HEART_BEAT_INTERVAL_SECONDS, ROOM_USER_EVENT, ROOM_TEAM_EVENT, ROOM_VM_READY, ROOMS_TEAMS_MODAL_DISABLED } from './room.constants';
import { invalidateCachedRunningVms, useGetRoomDetailQuery, useGetRoomTasksQuery, useGetScoreboardDataQuery, useJoinRoomMutation } from './room.slice';
import { StyledConfettiWrapper } from './components/completion-modal/completion-modal.styles';
import { RoomTeamEvent, RoomUserEvent } from './room.types';
import { HelperTooltipWrapper } from './components/helper-tooltips';
import { LegacyNetworks, Networks } from './components/networks';
import { VideoPremium } from './components/video-premium';
import { useIsEchoEnabledInCurrentRoom } from './room.helpers';
import { invalidateAnswerQuestion } from './components/question-and-answer-item/question-and-answer-item.slice';
import { RoomNpsForm } from './components/room-nps-form/room-nps-form';
import { useCombineStartMachineAttackboxWidgetV1Experiment, useCombineStartMachineAttackboxWidgetV2Experiment } from './hooks/use-combine-start-machine-attackbox-widget-experiment';
import { ActiveMachinesInformation } from './components/active-machines-information';
import { useShouldShowStudentsOnlyBanner } from './hooks/use-should-show-students-only-banner';
import { useTaskBreakdown } from './hooks/use-task-breakdown';
export const Room = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roomProgress, setRoomProgress] = useState<number>(0);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [isPathOutlineModalOpen, setIsPathOutlineModalOpen] = useState(searchParams.has('path'));
  const navigate = useNavigate();
  const {
    width
  } = useViewport();
  const {
    roomCode
  } = useParams();
  const {
    state: historyState,
    search: urlQueryParams
  } = (useLocation() as {
    state: LocationState;
    search: string;
  });
  const {
    tutorPanelOpen,
    vmPanelOpen,
    tabs: splitScreenTabs
  } = useAppSelector(state => state.splitScreen);
  const isSplitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const [teamRoomModal, setTeamRoomModal] = useState(false);
  const isAttackboxWidgetV1ExperimentEnabled = useCombineStartMachineAttackboxWidgetV1Experiment();
  const {
    isEnabled: isAttackboxWidgetV2ExperimentEnabled
  } = useCombineStartMachineAttackboxWidgetV2Experiment();
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const isInFireshipSession = useCampaignSession(CampaignName.FIRESHIP_2025);
  useEffect(() => {
    dispatch(setCurrentModal(ModalType.FIRESHIP_CAMPAIGN));
  }, [dispatch]);
  if (!roomCode) throw new Error('No roomCode in url params');
  const {
    data: roomData,
    isLoading,
    isError,
    error,
    isSuccess
  } = useGetRoomDetailQuery(roomCode);
  const {
    data: userData,
    isSuccess: isUserAuth
  } = useGetUserQuery();
  const roomDetails = roomData?.data;
  const [shouldShowStudentsOnlyBanner, onStudentsOnlyBannerClose] = useShouldShowStudentsOnlyBanner(roomDetails?.code, roomDetails?.teamSettings);
  const {
    data: currentTeamData,
    isLoading: isCurrentTeamDataLoading
  } = useGetCurrentTeamQuery(undefined, {
    skip: !roomDetails?.teams || !isUserAuth || !roomDetails?.joined
  });
  const {
    data: scoreboardData,
    isLoading: isScoreboardLoading
  } = useGetScoreboardDataQuery({
    roomCode,
    limit: 10
  }, {
    skip: !roomData?.data || roomData?.data?.simpleRoom
  });
  useEffect(() => {
    dispatch(setRoomCode(roomCode));
  }, [dispatch, roomCode]);
  const {
    data: tasksData,
    isLoading: tasksIsLoading
  } = useGetRoomTasksQuery(roomCode, {
    skip: !isSuccess
  });
  const {
    currentTask,
    resetToFirstTask,
    advanceToNextTask,
    isTaskBreakdownSupported,
    isLastTask
  } = useTaskBreakdown({
    roomCode,
    tasks: tasksData?.data
  });
  const [joinRoomTrigger] = useJoinRoomMutation();
  const {
    data: glossaryData,
    isSuccess: isGlossarySuccess
  } = useGetGlossaryQuery();
  const {
    data: featureFlagsQueryData
  } = useGetFeatureFlagsQuery();
  const [trackViewPage] = useTrackViewPageMutation();
  const {
    data: azureLabs
  } = useGetAzureLabsQuery({
    roomId: roomData?.data?._id
  }, {
    skip: !roomData?.data?.cloud?.azure || !roomData?.data?._id
  });
  const currentActiveLab = (azureLabs?.data ?? [])?.sort((a, b) => a.expires < b.expires ? 1 : -1)?.find(lab => lab.roomId === roomData?.data?._id);
  useEffect(() => {
    const trackPageLoaded = async () => {
      if (userData?.data?.user?._id) {
        await trackViewPage({
          name: roomCode,
          type: PageTypes.ROOM
        });
      }
    };
    trackPageLoaded().catch(() => {
      // silently discard errors. We don't mind if tracking events aren't fired
    });
  }, [roomCode, trackViewPage, userData?.data?.user._id]);
  const isAzureTrainingEnabled = useMemo(() => featureFlagsQueryData?.data?.some(flag => flag.name === FeatureFlagName.AZURE_CLOUD_TRAINING && flag.enabled) ?? false, [featureFlagsQueryData?.data]);
  const redirectToHacktivities = useCallback(() => {
    navigate('/hacktivities/search');
  }, [navigate]);

  // This hooks is responsible for getting the AttackBox info and the VMs info
  const {
    runningInstancesData
  } = useGetVms(roomCode);

  // This is to activate helper tooltips
  useHelperTooltip(roomCode);
  const glossaryTooltip = useRoomGlossaryTooltip(`room_content`, glossaryData?.data ?? []);
  const handleTaskContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    glossaryTooltip.handleClick(event);
  };

  // The only function that will be used to join the room
  const joinRoom = useCallback(async () => {
    await joinRoomTrigger({
      roomCode,
      pageReferrer: historyState?.pageReferrer,
      type: historyState?.type
    }).unwrap();
  }, [joinRoomTrigger, roomCode, historyState?.pageReferrer, historyState?.type]);

  // This is added to add the backward compatibility for /jr/ links
  // If we pass the URL like /jr/roomCode, it will be redirected to /room/roomCode?auto-join=true
  useLayoutEffect(() => {
    if (searchParams.get('auto-join')) {
      setSearchParams(prev => {
        prev.delete('auto-join');
        return prev;
      });
      if (!roomData?.data?.joined) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        joinRoom();
      }
    }
  }, [isSuccess, joinRoom, roomData?.data?.joined, searchParams, setSearchParams]);

  // Navigate to hacktivities search if room details information is not available
  useEffect(() => {
    if (isError && isQueryErrorWithMessage(error) && error?.status === HttpStatus.NOT_FOUND) {
      NotificationDispatch('error', ERROR_ROOM_NOT_FOUND);
      redirectToHacktivities();
    }
  }, [navigate, isError, error, redirectToHacktivities]);
  useEffect(() => {
    if (roomData?.data?.status === RoomDetailsStatusCodes.SUBSCRIBER_ONLY) {
      let whySubscribe = `/why-subscribe?roomCode=${roomCode}`;
      if (urlQueryParams) {
        whySubscribe += `&${urlQueryParams.slice(1)}`;
      }
      navigate(whySubscribe);
    }
  }, [navigate, roomData?.data?.status, roomCode, urlQueryParams]);
  useEffect(() => {
    const isRoomsRebuildAWSRoomsEnabled = featureFlagsQueryData?.data?.some(flag => flag.name === FeatureFlagName.ROOMS_REBUILD_AWS_ROOMS && flag.enabled) ?? false;
    if (!isRoomsRebuildAWSRoomsEnabled && (roomData?.data?.status === RoomDetailsStatusCodes.REQUIRES_CLOUD_AWS_ACTIVE || roomData?.data?.cloud)) {
      const legacyUrlString = getLegacyUrlString(new URL(window.location.href));
      window.location.replace(legacyUrlString);
    }
  }, [featureFlagsQueryData?.data, roomData?.data?.cloud, roomData?.data?.status]);

  // Calculate room progress
  useEffect(() => {
    if (tasksData?.data && tasksData?.status === 'success') {
      const questions = tasksData?.data?.map(task => task.questions) ?? [];
      const flattenedQuestions = questions.filter(Boolean).flat();
      const numberOfCorrectlyAnsweredQuestions = flattenedQuestions.reduce((total: number, item: RoomTaskQuestion) => total + (item.progress?.correct ? 1 : 0), 0);
      const percentValue = Math.floor(numberOfCorrectlyAnsweredQuestions / flattenedQuestions.length * 100);
      setRoomProgress(percentValue);
    }
  }, [tasksData?.data, tasksData?.status]);

  // Clearing split screen and close tutor on every mount
  useLayoutEffect(() => () => {
    dispatch(clearSplitScreen());
    dispatch(closeTutor());
    dispatch(clearTutorState());
  }, [dispatch]);
  const taskDetails = tasksData?.data;
  const isPrivateRoom = isError && isQueryErrorWithMessage(error) && error.status === HttpStatus.FORBIDDEN;
  const failedDetailsRequest = roomData?.status === 'fail';
  const isBannedRoom = failedDetailsRequest && roomDetails?.status === RoomDetailsStatusCodes.BANNED;
  const isLockedRoom = failedDetailsRequest && roomDetails?.status === RoomDetailsStatusCodes.LOCKED;
  const isAwsSubscriptionRequired = failedDetailsRequest && roomDetails?.status === RoomDetailsStatusCodes.REQUIRES_CLOUD_AWS_ACTIVE;
  const isAzureSubscriptionRequired = failedDetailsRequest && roomDetails?.status === RoomDetailsStatusCodes.REQUIRES_CLOUD_AZURE_ACTIVE;
  const isJoined = Boolean(roomDetails?.joined);
  const isNetworkActive = Boolean(roomDetails?.network?.active);
  const isNetworkV2 = Boolean(roomDetails?.networkId);
  const [isRetiredRoom] = useState(false);
  const [isConfettiVisible, setIsConfettiVisible] = useState(false);
  const [openInstanceTerminationModal, setOpenInstanceTerminationModal] = useState<boolean>(false);
  const isRoomCompleted = useAppSelector(state => state.room.isRoomCompleted);
  const activeMachine = useAppSelector(state => state.room.activeMachine);
  const vmStartType = useAppSelector(state => state.room.vmStartType);
  const attackBoxPrivateIP = useAppSelector(state => state.splitScreen.attackBoxPrivateIP);
  const activeAttackBoxInstance = useMemo(() => runningInstancesData?.find(instance => instance.remote.privateIP === attackBoxPrivateIP && instance.remote.active), [attackBoxPrivateIP, runningInstancesData]);
  const [isCongratulationsFullScreenOpen, setIsCongratulationsFullScreenOpen] = useState(isRoomCompleted);
  const showTutor = useIsEchoEnabledInCurrentRoom();
  const showNpsForm = !!(roomDetails?.joined && roomDetails?.shouldSeeRoomNpsForm && roomProgress >= 30);
  const hasMultipleVMs = useMemo(() => taskDetails ? taskDetails.filter(task => [TaskType.VM, TaskType.THM].includes(task.type)).length > 1 : false, [taskDetails]);
  useEffect(() => {
    if (isAwsSubscriptionRequired) {
      redirectToPath('cloud-access');
    }
  }, [isAwsSubscriptionRequired]);
  useEffect(() => {
    if (isAzureSubscriptionRequired) {
      redirectToPath('cloud-access');
    }
  }, [isAzureSubscriptionRequired]);
  useRoomHeartBeat(roomDetails?._id, isUserAuth, roomDetails?.userCompleted || isRoomCompleted, ROOM_HEART_BEAT_INTERVAL_SECONDS);
  useEffect(() => {
    if (isRoomCompleted) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      setIsConfettiVisible(true);
      setIsCongratulationsFullScreenOpen(true);
    }
  }, [isRoomCompleted]);
  useEffect(() => {
    if (isCongratulationsFullScreenOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isCongratulationsFullScreenOpen]);
  useEffect(() => {
    if (!roomDetails) return;
    if (isCurrentTeamDataLoading) return;
    const userHasTeam = !!currentTeamData?.data;
    const currentTeamId = (currentTeamData?.data?._id as string);
    dispatch(setHasUserJoinedRoom(roomDetails?.joined));
    dispatch(setRemAttackBoxBtn(roomDetails?.options?.remAttackBoxBtn ?? true));
    const handleRoomEvent = (data: unknown) => {
      const eventData = (data as RoomUserEvent);
      if (eventData.type === RoomUserEventType.INSTANCES_INTERRUPTION) {
        setOpenInstanceTerminationModal(true);
        invalidateCachedRunningVms();
      }
    };
    const handleRoomVmReady = (data: unknown) => {
      const {
        isVmReady
      } = (data as {
        isVmReady: boolean;
      });
      dispatch(setIsVmReady(isVmReady));
    };
    const handleRoomTeamEvent = (data: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        type,
        teamId,
        isRoomCompleted: isRoomCompletedByMember
      } = (data as RoomTeamEvent);
      if (type === 'answer-question') {
        invalidateAnswerQuestion();
      }
      if (isRoomCompletedByMember && userHasTeam && teamId === currentTeamId) {
        dispatch(setIsRoomComplete(true));
      }
    };
    if (roomDetails?.joined) {
      emitSocketEvent(ROOM_EVENTS_JOIN, roomDetails._id);
      addSocketListener(ROOM_USER_EVENT, handleRoomEvent);
      addSocketListener(ROOM_VM_READY, handleRoomVmReady);
    }
    if (roomDetails?.teams && userHasTeam) {
      emitSocketEvent(ROOM_JOIN_TEAM, {
        roomCode: roomDetails.code,
        teamId: currentTeamId
      });
      addSocketListener(ROOM_TEAM_EVENT, handleRoomTeamEvent);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      removeSocketListener(ROOM_USER_EVENT);
      removeSocketListener(ROOM_TEAM_EVENT);
      removeSocketListener(ROOM_VM_READY);
      dispatch(setIsRoomComplete(false));
      dispatch(setHasUserJoinedRoom(false));
    };
  }, [roomDetails, dispatch, currentTeamData?.data, isCurrentTeamDataLoading]);
  const openPathOutline = () => setIsPathOutlineModalOpen(true);
  const specialRoom = isBannedRoom || isLockedRoom || isRetiredRoom;
  const isSpecialOrPrivateRoom = specialRoom || isPrivateRoom;
  const isVideoActive = roomDetails?.video?.active && roomDetails?.video?.autoOpen || showVideo;
  const isVideoVisibleToUserInSimpleRoom = roomDetails?.video?.free ? isVideoActive : isVideoActive && userData?.data?.user?.isPremium;
  const isVideoVisibleToUserInComplexRoom = roomDetails?.video?.free ? roomDetails?.video?.active || showVideo : (roomDetails?.video?.active || showVideo) && userData?.data?.user?.isPremium;
  const isVideoPremiumVisible = !roomDetails?.video?.free && isVideoActive && !userData?.data?.user?.isPremium;
  const showSocSimAdBanner = featureFlagsQueryData?.data?.find(flag => flag.name === FeatureFlagName.SOC_SIM_AD_BANNER_IN_PATHS)?.enabled && ['soclevel1', 'soclevel2'].includes(roomData?.data?.breadcrumbs?.path?.code ?? '');
  useEffect(() => {
    if (isCurrentTeamDataLoading) return;
    const userHasTeam = Boolean(currentTeamData?.data);
    if (roomDetails?.teams && !userHasTeam && !ROOMS_TEAMS_MODAL_DISABLED.includes(roomDetails?.code.toLowerCase())) {
      setTeamRoomModal(true);
    }
  }, [roomDetails?.code, roomDetails?.teams, currentTeamData?.data, isCurrentTeamDataLoading]);

  // DAU integration, SCORM package
  const scormRoom = searchParams.get('scormRoom');
  useEffect(() => {
    if (roomProgress && scormRoom === roomCode) {
      window.parent.postMessage({
        type: 'roomProgress',
        score: roomProgress
      }, '*');
    }
  }, [roomProgress, scormRoom, roomCode]);
  const renderRoomHeaderOrBanner = () => isTaskBreakdownSupported ? <RoomHeader roomDetails={roomDetails} streak={userData?.data?.user?.streak} isSpecialOrPrivateRoom={isSpecialOrPrivateRoom} specialRoom={specialRoom} isBannedRoom={isBannedRoom} isPrivateRoom={isPrivateRoom} isLockedRoom={isLockedRoom} isRetiredRoom={isRetiredRoom} setRoomProgress={setRoomProgress} roomProgress={roomProgress} setShowVideo={setShowVideo} splitScreenTabs={splitScreenTabs} splitScreenOpen={isSplitScreenOpen} vmStartType={vmStartType} openPathOutline={openPathOutline} isSplitScreenOpen={isSplitScreenOpen} resetToFirstTask={resetToFirstTask} currentTask={currentTask} /> : <RoomBanner roomDetails={roomDetails} isLoading={isLoading} isSpecialOrPrivateRoom={isSpecialOrPrivateRoom} specialRoom={specialRoom} isBannedRoom={isBannedRoom} isPrivateRoom={isPrivateRoom} isLockedRoom={isLockedRoom} isRetiredRoom={isRetiredRoom} setRoomProgress={setRoomProgress} roomProgress={roomProgress} setShowVideo={setShowVideo} splitScreenTabs={splitScreenTabs} splitScreenOpen={isSplitScreenOpen} vmStartType={vmStartType} />;
  const renderActiveMachinesInformation = () => {
    if (isTaskBreakdownSupported || isAttackboxWidgetV2ExperimentEnabled) {
      return <ActiveMachinesInformation targetInstance={activeMachine} attackBoxInstance={activeAttackBoxInstance} roomCode={roomCode} hasMultipleVMs={hasMultipleVMs} tasks={taskDetails} currentTask={currentTask} isTaskBreakdownSupported={isTaskBreakdownSupported} />;
    }
    if (!activeMachine) {
      return null;
    }
    return <ActiveMachineInfo activeMachine={activeMachine} roomTitle={roomDetails?.title} remAttackBoxBtn={roomDetails?.options?.remAttackBoxBtn} roomCode={roomCode} data-sentry-element="ActiveMachineInfo" data-sentry-component="renderActiveMachinesInformation" data-sentry-source-file="room.tsx" />;
  };
  const renderTasksOrRoomContent = () => {
    if (!isGlossarySuccess || !glossaryData?.data) {
      return null;
    }
    if (isTaskBreakdownSupported) {
      return <RoomContent glossaryData={glossaryData.data} roomCode={roomCode} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} joinRoom={joinRoom} hasUserJoinedRoom={roomData?.data?.joined} activeMachine={activeMachine} currentTask={currentTask} roomDetails={roomDetails} advanceToNextTask={advanceToNextTask} isLastTask={isLastTask} />;
    }
    return <>
        <Tasks activeMachine={activeMachine} attackBoxPrivateIP={attackBoxPrivateIP} tasks={taskDetails} status={tasksData?.status} message={tasksData?.message} isLoading={tasksIsLoading} roomId={roomDetails?._id} joinRoom={joinRoom} hasUserJoinedRoom={roomData?.data?.joined} roomCode={roomCode} roomType={roomDetails?.type} isPublicRoom={roomDetails?.public ?? false} isNetworkRoom={roomDetails?.network?.active ?? false} networkTooltipShown={userData?.data?.user?.tutorials?.networks ?? false} glossaryData={glossaryData.data} handleTaskContentClick={handleTaskContentClick} setActiveGlossary={glossaryTooltip.setActiveGlossary} cloud={{
        aws: roomDetails?.cloud?.aws,
        azure: {
          isEnabled: roomDetails?.cloud?.azure?.isEnabled ?? false,
          isAzureTrainingEnabled,
          isMultiTenant: roomDetails?.cloud?.azure?.isMultiTenant ?? false,
          azureLabs: azureLabs?.data,
          currentLab: currentActiveLab
        }
      }} hasMultipleVMs={hasMultipleVMs} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} autoAnswerQuestions={roomDetails?.autoAnswerQuestions ?? false} data-sentry-element="Tasks" data-sentry-source-file="room.tsx" />
        {showNpsForm && <RoomNpsForm roomCode={roomCode} roomProgress={roomProgress} />}
      </>;
  };
  const handleCongratulationFullScreenClose = () => {
    if (isTaskBreakdownSupported) {
      dispatch(setIsRoomComplete(false));
    }
  };
  return <>
      <SEO title={roomDetails?.title ?? 'Room details'} description={roomDetails?.description ?? 'Room description'} data-sentry-element="SEO" data-sentry-source-file="room.tsx">
        <script src="https://unpkg.com/@lottiefiles/lottie-player@2.0.4/dist/lottie-player.js" />
      </SEO>

      <HelperTooltipWrapper data-sentry-element="HelperTooltipWrapper" data-sentry-source-file="room.tsx" />

      {renderRoomHeaderOrBanner()}

      {teamRoomModal && <StandardActionModal preventOnOpenAutoFocus open={teamRoomModal} onOpenChange={() => setTeamRoomModal(false)} actionButtonTitle="Go to my teams" actionButtonColor="primary" handleModalAction={() => navigate('/manage-account/teams')} showIcon iconComponent={<StyledModalIcon data-icon="warning" icon={faWarning} />} modalTitleText="This is a team room" modalBodyText={<span>
              Only team achievements are displayed on the Scoreboard, but you can still complete this room. To join a
              team, go to <strong>Manage Account &gt; My Teams</strong>.
            </span>} dismissButtonTitle="Close" />}
      {!isSpecialOrPrivateRoom && !isTaskBreakdownSupported && <RoomProgressBar value={roomProgress} />}
      <StyledContainer $isTaskBreakdownSupported={isTaskBreakdownSupported} $isDarkMode={isDarkMode} data-sentry-element="StyledContainer" data-sentry-source-file="room.tsx">
        {roomData && !roomData.data?.joined && isUserAuth && <StyledInfoBox>
            <p>To access material, start machines and answer questions you need to join this room!</p>
            <StyledButton
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={joinRoom} variant="solid" color="primary">
              Join Room
            </StyledButton>
          </StyledInfoBox>}
        {!isUserAuth && <StyledInfoBox>
            <p>
              To access material, start machines and answer questions <Link to="/login">login</Link>.
            </p>
          </StyledInfoBox>}

        <InstanceTerminationModal open={openInstanceTerminationModal} onOpenChange={setOpenInstanceTerminationModal} data-sentry-element="InstanceTerminationModal" data-sentry-source-file="room.tsx" />

        {isSpecialOrPrivateRoom ? <StyledInfoBox>
            <p>
              {ERROR_CONTACT_MSG} <Link to="/contact"> contact us</Link>
            </p>
          </StyledInfoBox> : <StyledRoomContent id="room_content">
            {/* // NETWORKS */}
            {isNetworkV2 && taskDetails && roomDetails?.networkId && <Networks templateId={roomDetails.networkId} roomId={roomDetails._id} tasks={taskDetails} />}
            {!isNetworkV2 && isNetworkActive && tasksData && roomDetails?._id && <LegacyNetworks roomId={roomDetails._id} isJoined={isJoined} taskDetails={taskDetails || []} />}

            {shouldShowStudentsOnlyBanner && <AlertBannerContainer>
                <AlertBanner status={AlertBannerStatus.WARNING}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: next-line type issues
          message={<>
                      This challenge is limited to student teams only. To appear on the Chart and Scoreboard, you need
                      to be part of a team and have a student profile. Read more{' '}
                      <StyledLink to="https://help.tryhackme.com/en/articles/10617468-how-to-verify-as-a-student-without-a-student-email">
                        here
                      </StyledLink>
                    </>} dismissable handleBannerDismiss={onStudentsOnlyBannerClose} />
              </AlertBannerContainer>}

            {!!roomDetails && !roomDetails?.simpleRoom && !failedDetailsRequest && <ComplexRoomBox roomDetails={roomDetails} tasksDetails={taskDetails} scoreboardDetails={scoreboardData?.data} isScoreboardLoading={isScoreboardLoading} isVideoVisibleToUser={isVideoVisibleToUserInComplexRoom} />}

            {roomDetails?.simpleRoom && isVideoVisibleToUserInSimpleRoom && <StyledVideoSection>
                <Video videoDetails={roomDetails?.video} isTHMVideo={roomDetails?.video?.source === VideoSource.THM} />
              </StyledVideoSection>}

            {isVideoPremiumVisible && <VideoPremium />}

            {renderActiveMachinesInformation()}

            {isAzureTrainingEnabled && currentActiveLab && roomData?.data?._id && <ActiveLabInfo lab={currentActiveLab} />}

            {renderTasksOrRoomContent()}

            {showSocSimAdBanner && <SocSimAdContainer>
                <SocSimAdBanner />
              </SocSimAdContainer>}

            {!failedDetailsRequest && !isTaskBreakdownSupported && <RoomDetailsFooter roomDetails={roomDetails} isLoading={isLoading} />}
            {Boolean(glossaryTooltip.activeGlossary) && <StyledGlossaryTooltipWrapper position={glossaryTooltip.position} onClick={() => glossaryTooltip.setActiveGlossary(null)}>
                <StyledGlossaryTooltipContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <GlossaryTooltip {...glossaryTooltip.tooltipProps} />
                </StyledGlossaryTooltipContent>
              </StyledGlossaryTooltipWrapper>}
          </StyledRoomContent>}
      </StyledContainer>
      {isTaskBreakdownSupported && <RoomFooter tasks={tasksData?.data} isSplitScreenOpen={isSplitScreenOpen} currentTask={currentTask} roomCode={roomCode} />}
      {isConfettiVisible && <StyledConfettiWrapper>
          <Confetti recycle={false} width={width} />
        </StyledConfettiWrapper>}
      {isCongratulationsFullScreenOpen && createPortal(<CongratulationFullScreen currentRoom={roomData?.data} setIsCongratulationsFullScreenOpen={setIsCongratulationsFullScreenOpen} isLoading={isLoading} onClose={handleCongratulationFullScreenClose} />, document.body)}

      <PathOutlineModal open={!isInFireshipSession && isPathOutlineModalOpen} handleModalAction={() => setIsPathOutlineModalOpen(false)} handleCloseButtonAction={() => setIsPathOutlineModalOpen(false)} pathCode={searchParams.get('path') ?? ''} data-sentry-element="PathOutlineModal" data-sentry-source-file="room.tsx" />
      {showTutor && roomData?.data?._id && <Tutor roomCode={roomCode} roomId={roomData.data._id} persona={roomData.data.tutor?.persona} />}
    </>;
};